import Vue      from 'vue'

// Import Vuetify pre-compiled CSS (includes all Vuetify styles)
import 'vuetify/dist/vuetify.min.css'
// Custom styles are imported in App.vue to ensure proper Vite processing

// Dev helper: load component annotator in development only
if (import.meta.env.MODE !== 'production') {
  import('./plugins/dev-component-annotator')
    .then(() => console.log('[dev-component-annotator] loaded'))
    .catch(() => {});
}

import App      from './App.vue'
import vuetify  from './plugins/vuetify';
import Keycloak from "keycloak-js"
import VueI18n  from 'vue-i18n'
import I18nMessages    from "@/i18n/i18n";

// Ensure jQuery is available if needed in main.js, though usually unnecessary if global
import $ from 'jquery'

import VueCurrencyInput from 'vue-currency-input'
import { router } from "./routes"
import Vue2Editor from "vue2-editor";
import { RecycleScroller } from 'vue-virtual-scroller'
import VueDraggableResizable from 'vue-draggable-resizable'

import { loadConfig, getApiAuth, getApiDashboardUrl } from "@/config.js";

const pluginOptions = {
    globalOptions: { currency: 'EUR',}
}


Vue.use(VueCurrencyInput, pluginOptions)
Vue.config.productionTip = false
Vue.use(VueI18n)
Vue.use(Vue2Editor);

const numberFormats = {
    'de': {
        currency: {
            style: 'currency', currency: 'EUR'
        }
    }
}


const i18n = new VueI18n({
    locale: 'de',
    messages: I18nMessages.messages,
    numberFormats: numberFormats,
    silentFallbackWarn: true
});

let keycloak;
let authUrl;

loadConfig().then(() => {
  authUrl = getApiAuth();
  const keycloakConfig = {
    url: authUrl,
    realm: "liftmanagement",
    clientId: "app",
  };

  const initOptions = {
    url: authUrl,
    onLoad: "login-required",
    responseMode: "query",
    checkLoginIframe: false,
    useNonce: false,
  };

  keycloak = Keycloak(keycloakConfig);

  keycloak
    .init(initOptions)
    .then((auth) => {
      if (!auth) {
        window.location.replace(window.location.origin + "/");
      } else {
        Vue.config.errorHandler = function (err, vm, info) {
          console.error(err);

          const isProd = import.meta.env.MODE === "production";
          const message = isProd
            ? i18n.t("common.error.generic")
            : err?.message || String(err);

          // Emit error alert (snackbar)
          Vue.prototype.$bus.$emit(Vue.prototype.$events.alert.error, {
            message: message,
          });
        };

        const events = {
          refresh: "global-refresh",
          refreshdocuments: "global-refresh-documents",
          history: "global-history",
          filter: "global-filter",
          clearFilter: "global-clear-filter",
          search: "global-search",
          opensearch: "global-search-open",
          searchcustomer: "global-searchcustomer",
          loading: "global-loading",
          message: "global-message-popup",
          mail: "global-mail",
          drag: {
            end: "global-drag-end",
          },
          alert: {
            confirm: "global-confirm",
            message: "global-message",
            error: "global-error-message",
            warning: "global-error-warning",
          },
          dms: {
            fadein: "dms-fage-in",
          },
          dis: {
            assignments: "dis-assignments",
            inspection: "dis.inspection",
          },
        };

        Vue.prototype.$bus = new Vue();
        Vue.prototype.$events = events;

        Vue.component("virtual-scroller", RecycleScroller);
        Vue.component("vue-draggable-resizable", VueDraggableResizable);

        new Vue({
          i18n,
          vuetify,
          router,
          el: "#app",
          render: (h) => h(App, { props: { keycloak: keycloak } }),
        });
      }

      //Token Refresh
      setInterval(() => {
        keycloak
          .updateToken(60000)
          .then((refreshed) => {
            if (!refreshed) {
              console.warn(
                "Token not refreshed, valid for " +
                  Math.round(
                    keycloak.tokenParsed.exp +
                      keycloak.timeSkew -
                      new Date().getTime() / 1000,
                  ) +
                  " seconds",
              );
            }
          })
          .catch(() => {
            console.error("Failed to refresh token");
          });
      }, 60000);
    })
    .catch((e) => {
      console.error("Authenticated Failed", e);
    });
});


export default {
    i18n () {
        return i18n;
    },

    vuetify () {
        return vuetify;
    },

    token () {
        return keycloak.token;
    },

    jsonHeader () {
        return {
            Authorization: 'Bearer ' + keycloak.token,
            "Content-Type": 'application/json'
        };
    },

    header () {
        return {
            Authorization: 'Bearer ' + keycloak.token,
        };
    },

    response (result, error) {
        if (!result.ok) {
            if (result.status === 401) {
                this.logout();
            } else {
                result.json().then(data => {
                    if (error) error(data);
                });
            }

            return Promise.reject();
        }

        return result.json();
    },

    profile () {
        const token = keycloak.idTokenParsed;
        const firstname = token.given_name || '';
        const lastname = token.family_name || '';
        const avatar = (firstname ? firstname.substring(0,1) : '') + (lastname ? lastname.substring(0,1) : '');
        
        return {
            username : token.preferred_username,
            email    : token.email,
            name     : token.name,
            firstname: firstname,
            lastname : lastname,
            avatar   : avatar || token.preferred_username?.substring(0,2).toUpperCase() || '??',
            roles    : token.groups.sort(),
        };
    },

    isLiftmanager () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("LIFTMANAGER");
    },

    isPlanner () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("HCPLANNER");
    },

    isAdmin () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("LIFTMANAGER ADMIN");
    },

    isPrivileged () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("LIFTMANAGER PRIVILEGED");
    },

    isContactAdmin () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("CONTACT_ADMIN");
    },

    isAlfaUser () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("ALFA_USER");
    },

    isDataAdmin () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("IMPORT_USER");
    },

    isMasterdataAdmin () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("MASTERDATA_ADMIN");
    },

    isUnitAdmin () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("UNIT_ADMIN");
    },

    isDispatcher () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("DISPATCHER");
    },

    isRestrictedDispatcher () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("DISPATCHER_RESTRICTED");
    },

    isDisponent () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("DISPONENT");
    },

    isSettingsAdmin () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("SETTINGS_ADMIN");
    },

    isGuardReports () {
        const groups = keycloak.idTokenParsed.groups;
        return groups.includes("GUARD_REPORTS");
    },

    isProd () {
        return process.env.NODE_ENV === "production" && authUrl === 'https://liftmanager.io/auth';
    },

    url: {
        dashboard () {
            return getApiDashboardUrl();
        }
    },

    logout() {
        keycloak.logout();
    }

}
